/**
 * Phone Parser Utilities
 * Wrappers around libphonenumber-js for common operations
 */

import {
  isValidPhoneNumber,
  parsePhoneNumber,
  getExampleNumber,
  getCountryCallingCode,
  type CountryCode,
  type PhoneNumber,
} from 'libphonenumber-js';
import examples from 'libphonenumber-js/mobile/examples';

import type { DigitInfo, PhoneValidationError } from '../types';
import { getCountryInfo, getExpectedDigits, CALLING_CODE_TO_COUNTRY } from './countryMetadata';

/**
 * Parse phone number string
 * Returns null if parsing fails
 */
export function parsePhone(value: string): PhoneNumber | null {
  if (!value) return null;

  try {
    return parsePhoneNumber(value);
  } catch {
    return null;
  }
}

/**
 * Check if phone number is valid
 */
export function isPhoneValid(value: string): boolean {
  if (!value) return false;
  return isValidPhoneNumber(value);
}

/**
 * Get country calling code
 */
export function getCallingCode(country: CountryCode): string {
  try {
    return getCountryCallingCode(country);
  } catch {
    return '';
  }
}

/**
 * Extract national number (digits only, without country code)
 */
export function extractNationalNumber(value: string, country: CountryCode): string {
  if (!value) return '';

  const parsed = parsePhone(value);
  if (parsed?.nationalNumber) {
    return parsed.nationalNumber;
  }

  // Fallback: manually strip country code
  try {
    const callingCode = getCallingCode(country);
    if (callingCode && value.startsWith(`+${callingCode}`)) {
      return value.slice(callingCode.length + 1).replace(/\D/g, '');
    }
  } catch {
    // Ignore
  }

  // Last fallback: strip leading + and any digits up to first non-digit
  return value.replace(/^\+\d{1,4}/, '').replace(/\D/g, '');
}

/**
 * Count digits in national number
 */
export function countNationalDigits(value: string, country: CountryCode): number {
  const nationalNumber = extractNationalNumber(value, country);
  return nationalNumber.length;
}

/**
 * Get digit info for validation display
 */
export function getDigitInfo(value: string, country: CountryCode): DigitInfo {
  const currentDigits = countNationalDigits(value, country);
  const { min, max } = getExpectedDigits(country);
  const hasInfo = !!getCountryInfo(country);

  const isValid = currentDigits >= min && currentDigits <= max;
  const expected = min === max ? String(min) : `${min}-${max}`;

  return {
    current: currentDigits,
    expected,
    expectedMin: min,
    expectedMax: max,
    isValid,
    hasInfo,
  };
}

/**
 * Get example phone number for country
 */
export function getExamplePhone(country: CountryCode): string {
  try {
    const example = getExampleNumber(country, examples);
    if (example) {
      return example.formatNational();
    }
  } catch {
    // Fallback to metadata
  }

  const info = getCountryInfo(country);
  return info?.format || 'XXX XXX XXXX';
}

/**
 * Detect country from phone value
 * Handles both +XX and XX formats
 * Returns null if cannot detect
 */
export function detectCountryFromValue(value: string): CountryCode | null {
  if (!value) return null;

  // Method 1: Try parsing with libphonenumber-js (works with +)
  const parsed = parsePhone(value);
  if (parsed?.country) {
    return parsed.country as CountryCode;
  }

  // Method 2: Extract calling code and lookup
  // Handle both "+62" and "62" formats
  const digits = value.replace(/\D/g, '');
  const startsWithPlus = value.startsWith('+');

  if (startsWithPlus || digits.length >= 1) {
    // Try progressively longer prefixes (3, 2, 1 digits)
    for (const length of [3, 2, 1]) {
      const prefix = startsWithPlus
        ? value.slice(1, 1 + length).replace(/\D/g, '')
        : digits.slice(0, length);

      if (prefix.length === length) {
        const country = CALLING_CODE_TO_COUNTRY[prefix];
        if (country) return country;
      }
    }
  }

  return null;
}

/**
 * Detect country from raw digits (without +)
 * Used for smart prefix detection when user types country code
 */
export function detectCountryFromDigits(digits: string): CountryCode | null {
  if (!digits) return null;

  const cleanDigits = digits.replace(/\D/g, '');
  if (cleanDigits.length < 1 || cleanDigits.length > 4) return null;

  // Try progressively longer prefixes
  for (const length of [3, 2, 1]) {
    if (cleanDigits.length >= length) {
      const prefix = cleanDigits.slice(0, length);
      const country = CALLING_CODE_TO_COUNTRY[prefix];
      if (country) return country;
    }
  }

  return null;
}

/**
 * Format phone number for display
 */
export function formatPhoneNumber(value: string): string {
  if (!value) return '';

  const parsed = parsePhone(value);
  if (parsed) {
    return parsed.formatInternational();
  }

  return value;
}

/**
 * Build phone value from country and national number
 */
export function buildPhoneValue(country: CountryCode, nationalNumber: string): string {
  const callingCode = getCallingCode(country);
  const digitsOnly = nationalNumber.replace(/\D/g, '');
  return callingCode ? `+${callingCode}${digitsOnly}` : digitsOnly;
}

/**
 * Validate phone and return structured error
 */
export function validatePhone(
  value: string,
  country: CountryCode,
  required: boolean = false
): PhoneValidationError | null {
  // Empty check
  if (!value || value === `+${getCallingCode(country)}`) {
    return required ? { code: 'REQUIRED' } : null;
  }

  // Check for invalid characters
  const cleanValue = value.replace(/^\+/, '');
  if (/[^\d\s\-()]/.test(cleanValue)) {
    return { code: 'INVALID_CHARS' };
  }

  // Get digit info
  const digitInfo = getDigitInfo(value, country);

  // Check digit count
  if (digitInfo.current < digitInfo.expectedMin) {
    return {
      code: 'TOO_SHORT',
      expected: digitInfo.expectedMin,
      actual: digitInfo.current,
    };
  }

  if (digitInfo.current > digitInfo.expectedMax) {
    return {
      code: 'TOO_LONG',
      expected: digitInfo.expectedMax,
      actual: digitInfo.current,
    };
  }

  // Final validation with libphonenumber-js
  if (!isPhoneValid(value)) {
    return {
      code: 'INVALID_FORMAT',
      country: country,
    };
  }

  return null;
}

/**
 * Get human-readable error message
 */
export function getErrorMessage(
  error: PhoneValidationError | null,
  countryName?: string
): string | null {
  if (!error) return null;

  switch (error.code) {
    case 'REQUIRED':
      return 'Phone number is required';
    case 'TOO_SHORT':
      return `Phone number needs ${(error.expected || 0) - (error.actual || 0)} more digits`;
    case 'TOO_LONG':
      return `Phone number has ${(error.actual || 0) - (error.expected || 0)} extra digits`;
    case 'INVALID_FORMAT':
      return countryName
        ? `This doesn't match ${countryName} phone format`
        : 'Invalid phone number format';
    case 'INVALID_CHARS':
      return 'Only digits are allowed';
    default:
      return 'Invalid phone number';
  }
}
